import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Plane, User, Mail, Phone, Calendar, FileText, ArrowRight } from 'lucide-react';

const ApplyNowPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    nationality: '',
    destination: searchParams.get('destination') || '',
    visaType: searchParams.get('type') || 'Tourist',
    travelDate: '',
    additionalInfo: ''
  });

  const [errors, setErrors] = useState({});

  const destinations = [
    { 
      value: 'United States', 
      translationKey: 'countryUnitedStates',
      flag: '🇺🇸', 
      types: [
        { value: 'Tourist B1/B2', translationKey: 'visaTypeTouristB1B2' },
        { value: 'Business', translationKey: 'visaTypeBusiness' },
        { value: 'Student F1', translationKey: 'visaTypeStudentF1' }
      ]
    },
    { 
      value: 'United Kingdom', 
      translationKey: 'countryUnitedKingdom',
      flag: '🇬🇧', 
      types: [
        { value: 'Visitor', translationKey: 'visaTypeVisitor' },
        { value: 'Business', translationKey: 'visaTypeBusiness' },
        { value: 'Student', translationKey: 'visaTypeStudent' }
      ]
    },
    { 
      value: 'Schengen Area', 
      translationKey: 'countrySchengenArea',
      flag: '🇪🇺', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' },
        { value: 'Visit', translationKey: 'visaTypeVisit' }
      ]
    },
    { 
      value: 'France', 
      translationKey: 'countryFrance',
      flag: '🇫🇷', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' },
        { value: 'Visit', translationKey: 'visaTypeVisit' }
      ]
    },
    { 
      value: 'Italy', 
      translationKey: 'countryItaly',
      flag: '🇮🇹', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' },
        { value: 'Visit', translationKey: 'visaTypeVisit' }
      ]
    },
    { 
      value: 'Spain', 
      translationKey: 'countrySpain',
      flag: '🇪🇸', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' },
        { value: 'Visit', translationKey: 'visaTypeVisit' }
      ]
    },
    { 
      value: 'Switzerland', 
      translationKey: 'countrySwitzerland',
      flag: '🇨🇭', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' },
        { value: 'Visit', translationKey: 'visaTypeVisit' }
      ]
    },
    { 
      value: 'Canada', 
      translationKey: 'countryCanada',
      flag: '🇨🇦', 
      types: [
        { value: 'Visitor', translationKey: 'visaTypeVisitor' },
        { value: 'Student', translationKey: 'visaTypeStudent' },
        { value: 'Work', translationKey: 'visaTypeWork' }
      ]
    },
    { 
      value: 'Australia', 
      translationKey: 'countryAustralia',
      flag: '🇦🇺', 
      types: [
        { value: 'Visitor 600', translationKey: 'visaTypeVisitor600' },
        { value: 'Working Holiday', translationKey: 'visaTypeWorkingHoliday' },
        { value: 'Student', translationKey: 'visaTypeStudent' }
      ]
    },
    { 
      value: 'Japan', 
      translationKey: 'countryJapan',
      flag: '🇯🇵', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' }
      ]
    },
    { 
      value: 'China', 
      translationKey: 'countryChina',
      flag: '🇨🇳', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' }
      ]
    },
    { 
      value: 'South Africa', 
      translationKey: 'countrySouthAfrica',
      flag: '🇿🇦', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' }
      ]
    },
    { 
      value: 'Russia', 
      translationKey: 'countryRussia',
      flag: '🇷🇺', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' }
      ]
    },
    { 
      value: 'Cyprus', 
      translationKey: 'countryCyprus',
      flag: '🇨🇾', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' }
      ]
    },
    { 
      value: 'Saudi Arabia', 
      translationKey: 'countrySaudiArabia',
      flag: '🇸🇦', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' }
      ]
    },
    { 
      value: 'Turkey', 
      translationKey: 'countryTurkey',
      flag: '🇹🇷', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' }
      ]
    },
    { 
      value: 'Singapore', 
      translationKey: 'countrySingapore',
      flag: '🇸🇬', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' }
      ]
    },
    { 
      value: 'New Zealand', 
      translationKey: 'countryNewZealand',
      flag: '🇳🇿', 
      types: [
        { value: 'Visitor', translationKey: 'visaTypeVisitor' },
        { value: 'Student', translationKey: 'visaTypeStudent' },
        { value: 'Work', translationKey: 'visaTypeWork' }
      ]
    },
    { 
      value: 'Indonesia (Bali)', 
      translationKey: 'countryIndonesia',
      flag: '🇮🇩', 
      types: [
        { value: 'Tourist', translationKey: 'visaTypeTourist' },
        { value: 'Business', translationKey: 'visaTypeBusiness' }
      ]
    },
  ];

  const selectedDestination = destinations.find(d => d.value === formData.destination);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = t('applyErrorFullName');
    if (!formData.email.trim()) {
      newErrors.email = t('applyErrorEmail');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('applyErrorEmailInvalid');
    }
    if (!formData.phone.trim()) newErrors.phone = t('applyErrorPhone');
    if (!formData.nationality.trim()) newErrors.nationality = t('applyErrorNationality');
    if (!formData.destination) newErrors.destination = t('applyErrorDestination');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Format WhatsApp message
    const message = `Hi! I'm interested in applying for a visa.

*Personal Details:*
📝 Name: ${formData.fullName}
📧 Email: ${formData.email}
📱 Phone: ${formData.phone}
🌍 Nationality: ${formData.nationality}

*Visa Information:*
✈️ Destination: ${formData.destination}
📋 Visa Type: ${formData.visaType}
${formData.travelDate ? `📅 Travel Date: ${formData.travelDate}` : ''}

${formData.additionalInfo ? `*Additional Information:*\n${formData.additionalInfo}` : ''}

Looking forward to your assistance!`;

    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = '971585862103';
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

    // Redirect to WhatsApp
    window.open(whatsappURL, '_blank');
    
    // Optionally redirect to home after a delay
    setTimeout(() => {
      navigate('/');
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-logo-blue to-logo-cyan text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-6">
            <Plane className="w-10 h-10" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            {t('applyTitle')}
          </h1>
          <p className="text-xl text-white/90">
            {t('applySubtitle')}
          </p>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Full Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  {t('applyFullName')} *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border-2 ${errors.fullName ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-logo-cyan focus:border-transparent outline-none transition-all`}
                  placeholder={t('applyFullNamePlaceholder')}
                />
                {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  {t('applyEmailAddress')} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border-2 ${errors.email ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-logo-cyan focus:border-transparent outline-none transition-all`}
                  placeholder={t('applyEmailPlaceholder')}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  {t('applyPhoneNumber')} *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border-2 ${errors.phone ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-logo-cyan focus:border-transparent outline-none transition-all`}
                  placeholder={t('applyPhonePlaceholder')}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              {/* Nationality */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('applyNationality')} *
                </label>
                <input
                  type="text"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border-2 ${errors.nationality ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-logo-cyan focus:border-transparent outline-none transition-all`}
                  placeholder={t('applyNationalityPlaceholder')}
                />
                {errors.nationality && <p className="text-red-500 text-sm mt-1">{errors.nationality}</p>}
              </div>

              {/* Destination */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('applyDestination')} *
                </label>
                <select
                  name="destination"
                  value={formData.destination}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border-2 ${errors.destination ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-logo-cyan focus:border-transparent outline-none transition-all`}
                >
                  <option value="">{t('applyDestinationPlaceholder')}</option>
                  {destinations.map(dest => (
                    <option key={dest.value} value={dest.value}>
                      {dest.flag} {t(dest.translationKey)}
                    </option>
                  ))}
                </select>
                {errors.destination && <p className="text-red-500 text-sm mt-1">{errors.destination}</p>}
              </div>

              {/* Visa Type */}
              {selectedDestination && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('applyVisaType')}
                  </label>
                  <select
                    name="visaType"
                    value={formData.visaType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-logo-cyan focus:border-transparent outline-none transition-all"
                  >
                    {selectedDestination.types.map(type => (
                      <option key={type.value} value={type.value}>
                        {t(type.translationKey)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Travel Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  {t('applyTravelDate')}
                </label>
                <input
                  type="date"
                  name="travelDate"
                  value={formData.travelDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-logo-cyan focus:border-transparent outline-none transition-all"
                />
              </div>

              {/* Additional Info */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <FileText className="w-4 h-4 inline mr-2" />
                  {t('applyAdditionalInfo')}
                </label>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-logo-cyan focus:border-transparent outline-none transition-all resize-none"
                  placeholder={t('applyAdditionalInfoPlaceholder')}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-full font-bold text-lg hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>{t('applyContinueBtn')}</span>
                <ArrowRight className="w-5 h-5" />
              </button>

              <p className="text-center text-sm text-gray-600">
                {t('applyRedirectMessage')}
              </p>
            </form>
          </div>

          {/* Why Apply With Us */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-white rounded-xl shadow-md">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="font-bold text-gray-900 mb-2">{t('applyBenefit1Title')}</h3>
              <p className="text-sm text-gray-600">{t('applyBenefit1Desc')}</p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-md">
              <div className="text-4xl mb-3">🛡️</div>
              <h3 className="font-bold text-gray-900 mb-2">{t('applyBenefit2Title')}</h3>
              <p className="text-sm text-gray-600">{t('applyBenefit2Desc')}</p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-md">
              <div className="text-4xl mb-3">💬</div>
              <h3 className="font-bold text-gray-900 mb-2">{t('applyBenefit3Title')}</h3>
              <p className="text-sm text-gray-600">{t('applyBenefit3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ApplyNowPage;
